/**
 * Nothing in the schema marks a feed as the hero, so mi6 matches it by slug.
 *
 * The slug is derived from the name on INSERT only (ContentFeed.createSlug ->
 * user-api utils/slugify), and slugify strips every non-alphanumeric character
 * rather than converting it: "Featured" becomes "featured", but "Featured on
 * Content home" would become "featuredoncontenthome". Name the feed with a
 * single word so this constant stays obvious.
 *
 * Because the slug is fixed at creation, renaming the feed no longer demotes it
 * to an ordinary topic row -- that was true while the slug was re-derived on
 * every save. What still matters is that the feed is CREATED with a name that
 * slugifies to this value. If no feed matches, the page renders no hero section
 * rather than breaking. A `kind` field on ContentFeed remains the durable fix.
 */
export const HERO_FEED_SLUG = 'featured'

/**
 * Slots in the featured band. There is only ever one hero feed, and it holds
 * exactly this many items -- exactly, not at most.
 *
 * A partly filled band is broken rather than merely sparse: the member app
 * sizes the hero cards from a full band, so with two items the second renders
 * LARGER than the first. That is why this is a hard block on publishing and on
 * visibility, where the topic rows' TOPIC_FEED_MIN_ITEMS is only advice.
 *
 * Enforced in MI6 only. setContentFeedBoosts still accepts any count up to
 * MAX_FEED_BOOSTS, so this is a guard rail rather than a guarantee.
 */
export const HERO_SLOT_COUNT = 3

/** Cards a topic row shows on Content home before "View all". */
export const TOPIC_FEED_MAX_ITEMS = 8

/**
 * Below this a topic row looks sparse next to the others. Advisory only -- the
 * picker states it, but nothing blocks publishing with fewer.
 */
export const TOPIC_FEED_MIN_ITEMS = 3

/**
 * The server's own ceiling: MAX_FEED_BOOSTS in ContentFeedBoostService, backed
 * by a CHECK (rank BETWEEN 1 AND 10). The product limits above are tighter and
 * are currently enforced client-side only, so this stays as the real backstop.
 */
export const MAX_FEED_BOOSTS = 10

/**
 * How many items this feed may hold. Both limits are product rules rather than
 * schema ones, so they live here until it is decided whether the server should
 * own them.
 */
export const maxItemsForFeed = (slug?: string): number =>
  slug === HERO_FEED_SLUG ? HERO_SLOT_COUNT : TOPIC_FEED_MAX_ITEMS
